/**
 * Build provenance for display in the UI.
 *
 * Values are injected at build time via Vite env (Dockerfile build args,
 * wired from the Jenkinsfile). `VITE_APP_VERSION` carries
 * `git describe --tags --long --always` (leading "v" stripped):
 *   - exactly on a tag  → "0.1.0-0-g4520721"   → shown as "0.1.0"
 *   - after a tag       → "0.1.0-5-g4520721"   → shown as "0.1.0 +5 · 4520721"
 *   - no tag reachable  → "4520721" (abbrev sha) → shown as-is
 *   - local dev (unset) → "dev"
 * `VITE_GIT_SHA` is the full commit sha, shown in the tooltip.
 */

const DESCRIBE_RE = /^(.+)-(\d+)-g([0-9a-f]+)$/i;

export function formatVersion(rawVersion: string, rawSha: string): string {
  const described = rawVersion.match(DESCRIBE_RE);
  if (described) {
    const [, tag, distanceStr, gsha] = described;
    const distance = Number(distanceStr);
    if (distance === 0) return tag;
    const short = rawSha ? rawSha.slice(0, 7) : gsha;
    return `${tag} +${distance} · ${short}`;
  }
  if (rawVersion) return rawVersion;
  if (rawSha) return rawSha.slice(0, 7);
  return "dev";
}

const rawVersion = (import.meta.env.VITE_APP_VERSION ?? "").trim();
const rawSha = (import.meta.env.VITE_GIT_SHA ?? "").trim();

export const appVersion = {
  /** Full commit sha the build was produced from (empty in dev). */
  sha: rawSha,
  /** Short label: tag, "tag +N · sha", abbrev sha, or "dev". */
  label: formatVersion(rawVersion, rawSha),
};
